/**
 * sse.ts — shared Server-Sent Events reader for the Deckorate streaming APIs.
 *
 * DeckorateAPI streams several long-running operations (slide generation,
 * slide transformation) as `text/event-stream`. The framing is identical in
 * every case — `event: <name>` / `data: <json>` separated by a blank line — so
 * the reader lives here once and each feature client supplies only its own
 * dispatch table and error mapping.
 *
 * Extracted from create-slide's slideGenerationApi.ts; the parsing semantics
 * are unchanged (multi-line `data:` joined with newlines, frames without a
 * `data:` line ignored, a trailing unterminated frame flushed at end-of-stream).
 */

/** Raised for stream-level failures that occur before any frame is parsed. */
export class SseStreamError extends Error {
  constructor(
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "SseStreamError";
  }
}

/** Receives each successfully parsed frame. `data` is the JSON-decoded `data:` payload. */
export type SseFrameHandler = (event: string, data: unknown) => void;

export interface ConsumeSseOptions {
  /**
   * Called when a frame's `data:` payload is not valid JSON. A malformed frame
   * is skipped rather than aborting the stream, so a single bad frame cannot
   * lose the frames that follow it — but it is never swallowed silently.
   */
  readonly onMalformedFrame?: (event: string, error: unknown) => void;
}

const EVENT_FIELD = "event:";
const DATA_FIELD = "data:";
const FRAME_SEPARATOR = "\n\n";
/** SSE's default event name when a frame omits the `event:` field. */
const DEFAULT_EVENT_NAME = "message";

/** Parse one raw frame and hand it to `onFrame`; skip it if it carries no data. */
function handleFrame(frame: string, onFrame: SseFrameHandler, options: ConsumeSseOptions): void {
  let eventName = DEFAULT_EVENT_NAME;
  const dataLines: string[] = [];

  for (const line of frame.split("\n")) {
    if (line.startsWith(EVENT_FIELD)) eventName = line.slice(EVENT_FIELD.length).trim();
    else if (line.startsWith(DATA_FIELD)) dataLines.push(line.slice(DATA_FIELD.length).trim());
  }

  // Comment-only frames (`: keep-alive`) and field-less frames carry no payload.
  if (dataLines.length === 0) return;

  try {
    onFrame(eventName, JSON.parse(dataLines.join("\n")));
  } catch (error) {
    options.onMalformedFrame?.(eventName, error);
  }
}

/**
 * Read an SSE body stream to completion, dispatching each frame to `onFrame`.
 *
 * Throws {@link SseStreamError} with code `NO_STREAM` when the response has no
 * readable body. Aborting the underlying request rejects the read with the
 * caller's abort reason, which propagates — callers own abort handling.
 */
export async function consumeSseStream(
  body: ReadableStream<Uint8Array> | null | undefined,
  onFrame: SseFrameHandler,
  options: ConsumeSseOptions = {},
): Promise<void> {
  if (!body) throw new SseStreamError("NO_STREAM", "Response had no body stream");

  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      // `stream: true` keeps multi-byte characters split across chunks intact.
      buffer += decoder.decode(value, { stream: true });

      let separatorAt: number;
      while ((separatorAt = buffer.indexOf(FRAME_SEPARATOR)) !== -1) {
        handleFrame(buffer.slice(0, separatorAt), onFrame, options);
        buffer = buffer.slice(separatorAt + FRAME_SEPARATOR.length);
      }
    }
  } finally {
    // Release the lock so an aborted stream doesn't leave the body pinned.
    reader.releaseLock();
  }

  // A well-behaved server terminates the last frame, but flush anything left so
  // a missing trailing blank line doesn't drop the final (often `result`) frame.
  if (buffer.trim()) handleFrame(buffer, onFrame, options);
}
